import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router';
import { BackLink } from '@/components/base/back-link/back-link';
import {
    ArrowLeft,
    Package,
    SearchLg,
    Eye
} from '@untitledui/icons';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { ProgressBar } from '@/components/base/progress-indicators/progress-indicators';
import { containerService } from '@/data/mock-containers';
import { collaborationService } from '@/data/mock-collaborations';
import { accountService } from '@/data/mock-accounts';
import type { Container, ContainerStatus, RiskLevel, DocumentStatus } from '@/types/container';
import type { Account } from '@/types/account';
import { useTranslation } from 'react-i18next';
import { useI18n } from '@/hooks/use-i18n';
import { storage } from '@/lib/local-storage';

export const ShipperDetailPage = () => {
    const { t } = useTranslation(['pages:shippers', 'pages:containers', 'types', 'common']);
    const { translateContainerStatus, translateDocumentStatus, translateRiskLevel } = useI18n();
    const { id } = useParams<{ id: string }>();
    const [shipper, setShipper] = useState<Account | null>(null);
    const [collaboration, setCollaboration] = useState<any>(null);
    const [containers, setContainers] = useState<Container[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<ContainerStatus | ''>('');
    const [riskLevelFilter, setRiskLevelFilter] = useState<RiskLevel | ''>('');
    const [documentStatusFilter, setDocumentStatusFilter] = useState<DocumentStatus | ''>('');

    const statusOptions = [
        { id: '', label: t('list.filters.allStatuses', { ns: 'pages:containers' }) },
        { id: 'In Transit', label: t('containerStatus.inTransit', { ns: 'types' }) },
        { id: 'Delivered', label: t('containerStatus.delivered', { ns: 'types' }) },
        { id: 'Delayed', label: t('containerStatus.delayed', { ns: 'types' }) },
        { id: 'At Port', label: t('containerStatus.atPort', { ns: 'types' }) }
    ];

    const riskLevelOptions = [
        { id: '', label: t('list.filters.allLevels', { ns: 'pages:containers' }) },
        { id: 'Low', label: t('riskLevel.low', { ns: 'types' }) },
        { id: 'Medium', label: t('riskLevel.medium', { ns: 'types' }) },
        { id: 'High', label: t('riskLevel.high', { ns: 'types' }) },
        { id: 'Critical', label: t('riskLevel.critical', { ns: 'types' }) }
    ];

    const documentStatusOptions = [
        { id: '', label: t('list.filters.allStatuses', { ns: 'pages:containers' }) },
        { id: 'Completo', label: t('documentStatus.complete', { ns: 'types' }) },
        { id: 'Pending', label: t('documentStatus.pending', { ns: 'types' }) },
        { id: 'Error', label: t('documentStatus.error', { ns: 'types' }) }
    ];

    useEffect(() => {
        const loadData = async () => {
            if (!id) return;

            setIsLoading(true);
            await new Promise(resolve => setTimeout(resolve, 500));

            // Get current user ID from localStorage
            const accountId = storage.getItem('accountId') || '2';

            // Load shipper account
            const shipperData = accountService.getById(id);
            if (!shipperData) {
                setIsLoading(false);
                return;
            }
            setShipper(shipperData);

            // Load collaboration
            const collaborations = collaborationService.getByReceiverId(accountId);
            const shipperCollaboration = collaborations.find(c => c.shipperId === id);
            setCollaboration(shipperCollaboration);

            // Load containers from this shipper
            const allContainers = containerService.getAll();
            const shipperContainers = allContainers.filter(c =>
                c.shipperId === id && c.receiverId === accountId
            );
            setContainers(shipperContainers);

            setIsLoading(false);
        };
        loadData();
    }, [id]);

    const getRiskLevelColor = (riskLevel: string) => {
        switch (riskLevel) {
            case 'Low': return 'success';
            case 'Medium': return 'warning';
            case 'High': return 'error';
            case 'Critical': return 'error';
            default: return 'gray';
        }
    };

    const getDocumentStatusColor = (status: string) => {
        switch (status) {
            case 'Completo': return 'success';
            case 'Pending': return 'warning';
            case 'Error': return 'error';
            default: return 'gray';
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'In Transit': return 'brand';
            case 'Delivered': return 'success';
            case 'Delayed': return 'error';
            case 'At Port': return 'warning';
            default: return 'gray';
        }
    };

    const getCollaborationStatusColor = (status: string) => {
        switch (status) {
            case 'Active': return 'success';
            case 'Pending': return 'warning';
            case 'Rejected': return 'error';
            case 'Suspended': return 'error';
            default: return 'gray';
        }
    };

    const getCollaborationStatusLabel = (status: string) => {
        switch (status) {
            case 'Active': return t('list.status.active');
            case 'Pending': return t('list.status.pending');
            case 'Rejected': return t('list.status.rejected');
            case 'Suspended': return t('list.status.suspended');
            default: return status;
        }
    };

    const filteredContainers = containers.filter(container => {
        const matchesSearch = !searchTerm ||
            container.containerNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            container.route.from.toLowerCase().includes(searchTerm.toLowerCase()) ||
            container.route.to.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = !statusFilter || container.status === statusFilter;
        const matchesRiskLevel = !riskLevelFilter || container.riskLevel === riskLevelFilter;
        const matchesDocumentStatus = !documentStatusFilter || container.documentStatus === documentStatusFilter;

        return matchesSearch && matchesStatus && matchesRiskLevel && matchesDocumentStatus;
    });

    // Calculate container statistics
    const containerStats = {
        total: containers.length,
        inTransit: containers.filter(c => c.status === 'In Transit').length,
        delivered: containers.filter(c => c.status === 'Delivered').length,
        delayed: containers.filter(c => c.status === 'Delayed').length,
        pendingDocuments: containers.filter(c => c.documentStatus === 'Pending').length,
        highRisk: containers.filter(c => c.riskLevel === 'High' || c.riskLevel === 'Critical').length,
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg text-tertiary">{t('detail.loading')}</p>
            </div>
        );
    }

    if (!shipper) {
        return (
            <div className="text-center py-12">
                <Package className="size-12 mx-auto mb-4 text-tertiary" />
                <h3 className="text-lg font-medium text-primary mb-2">{t('detail.notFound.title')}</h3>
                <p className="text-tertiary mb-4">{t('detail.notFound.message')}</p>
                <BackLink
                    fallbackTo="/receiver/shippers"
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-solid px-4 py-2.5 text-md font-semibold dark:text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover"
                >
                    <ArrowLeft className="size-5" />
                    {t('detail.backToList')}
                </BackLink>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <BackLink fallbackTo="/receiver/shippers" aria-label={t('detail.backToList')} />
                    <div>
                        <h1 className="text-2xl font-semibold text-primary">{shipper.legalName}</h1>
                        <p className="text-tertiary mt-1">
                            {shipper.country} • {shipper.email}
                        </p>
                    </div>
                </div>
                {collaboration && (
                    <Badge color={getCollaborationStatusColor(collaboration.status)} size="lg">
                        {getCollaborationStatusLabel(collaboration.status)}
                    </Badge>
                )}
            </div>

            {/* Shipper Info */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {/* Anagrafica */}
                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                    <h2 className="text-lg font-semibold text-primary mb-4">{t('detail.info.companyInfo')}</h2>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <div className="text-sm font-medium text-primary">{t('detail.info.fields.legalName')}</div>
                                <div className="text-sm text-tertiary">{shipper.legalName}</div>
                            </div>
                            <div>
                                <div className="text-sm font-medium text-primary">{t('detail.info.fields.entityType')}</div>
                                <div className="text-sm text-tertiary">{shipper.entityType}</div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <div className="text-sm font-medium text-primary">{t('detail.info.fields.taxId')}</div>
                                <div className="text-sm text-tertiary">{shipper.taxId}</div>
                            </div>
                            <div>
                                <div className="text-sm font-medium text-primary">{t('detail.info.fields.country')}</div>
                                <div className="text-sm text-tertiary">{shipper.country}</div>
                            </div>
                        </div>
                        <div>
                            <div className="text-sm font-medium text-primary">{t('detail.info.fields.address')}</div>
                            <div className="text-sm text-tertiary">
                                {shipper.address.street}, {shipper.address.city}, {shipper.address.state} {shipper.address.postalCode}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <div className="text-sm font-medium text-primary">{t('detail.info.fields.email')}</div>
                                <div className="text-sm text-tertiary">{shipper.email}</div>
                            </div>
                            <div>
                                <div className="text-sm font-medium text-primary">{t('detail.info.fields.phone')}</div>
                                <div className="text-sm text-tertiary">{shipper.phone || 'N/A'}</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Statistiche Container */}
                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                    <h2 className="text-lg font-semibold text-primary mb-4">{t('detail.info.containerStats')}</h2>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="text-center">
                                <div className="text-2xl font-semibold text-primary">{containerStats.total}</div>
                                <div className="text-sm text-tertiary">{t('detail.info.stats.total')}</div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-semibold text-brand-600">{containerStats.inTransit}</div>
                                <div className="text-sm text-tertiary">{t('detail.info.stats.inTransit')}</div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="text-center">
                                <div className="text-2xl font-semibold text-success-600">{containerStats.delivered}</div>
                                <div className="text-sm text-tertiary">{t('detail.info.stats.delivered')}</div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-semibold text-error-600">{containerStats.delayed}</div>
                                <div className="text-sm text-tertiary">{t('detail.info.stats.delayed')}</div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="text-center">
                                <div className="text-2xl font-semibold text-warning-600">{containerStats.pendingDocuments}</div>
                                <div className="text-sm text-tertiary">{t('detail.info.stats.pendingDocuments')}</div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-semibold text-error-600">{containerStats.highRisk}</div>
                                <div className="text-sm text-tertiary">{t('detail.info.stats.highRisk')}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Container List */}
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-primary">{t('detail.containers.title')}</h2>
                    <div className="text-sm text-tertiary">
                        {filteredContainers.length} {t('detail.containers.count')} {containers.length} {t('list.title', { ns: 'pages:containers' })}
                    </div>
                </div>

                {/* Filters */}
                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <Input
                            placeholder={t('detail.containers.searchPlaceholder')}
                            value={searchTerm}
                            onChange={setSearchTerm}
                            icon={SearchLg}
                            size="md"
                        />

                        <Select
                            placeholder={t('detail.containers.statusPlaceholder')}
                            items={statusOptions}
                            selectedKey={statusFilter}
                            onSelectionChange={(key) => setStatusFilter(key as ContainerStatus | '')}
                            size="md"
                        >
                            {(item) => (
                                <Select.Item key={item.id} id={item.id}>
                                    {item.label}
                                </Select.Item>
                            )}
                        </Select>

                        <Select
                            placeholder={t('detail.containers.riskPlaceholder')}
                            items={riskLevelOptions}
                            selectedKey={riskLevelFilter}
                            onSelectionChange={(key) => setRiskLevelFilter(key as RiskLevel | '')}
                            size="md"
                        >
                            {(item) => (
                                <Select.Item key={item.id} id={item.id}>
                                    {item.label}
                                </Select.Item>
                            )}
                        </Select>

                        <Select
                            placeholder={t('detail.containers.documentsPlaceholder')}
                            items={documentStatusOptions}
                            selectedKey={documentStatusFilter}
                            onSelectionChange={(key) => setDocumentStatusFilter(key as DocumentStatus | '')}
                            size="md"
                        >
                            {(item) => (
                                <Select.Item key={item.id} id={item.id}>
                                    {item.label}
                                </Select.Item>
                            )}
                        </Select>
                    </div>
                </div>

                {/* Containers Table */}
                <TableCard.Root>
                    <Table>
                        <Table.Header>
                            <Table.Head label={t('detail.containers.headers.containerId')} isRowHeader />
                            <Table.Head label={t('detail.containers.headers.route')} />
                            <Table.Head label={t('detail.containers.headers.progress')} />
                            <Table.Head label={t('detail.info.fields.eta')} />
                            <Table.Head label={t('detail.containers.headers.status')} />
                            <Table.Head label={t('detail.containers.headers.docStatus')} />
                            <Table.Head label={t('detail.containers.headers.riskLevel')} />
                            <Table.Head label={t('detail.containers.headers.value')} />
                            <Table.Head label={t('detail.containers.headers.actions')} />
                        </Table.Header>
                        <Table.Body>
                            {filteredContainers.map((container) => (
                                <Table.Row key={container.id}>
                                    <Table.Cell>
                                        <div className="font-medium text-primary">{container.containerNumber}</div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <div className="text-sm text-tertiary">
                                            {container.route.from} → {container.route.to}
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <div className="flex items-center gap-2">
                                            <ProgressBar
                                                value={container.progress}
                                                min={0}
                                                max={100}
                                            />
                                            <span className="text-sm text-tertiary">{container.progress}%</span>
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <span className="text-sm text-tertiary">
                                            {container.eta.toLocaleDateString('it-IT')}
                                        </span>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Badge color={getStatusColor(container.status)} size="sm">
                                            {translateContainerStatus(container.status)}
                                        </Badge>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Badge color={getDocumentStatusColor(container.documentStatus)} size="sm">
                                            {translateDocumentStatus(container.documentStatus)}
                                        </Badge>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Badge color={getRiskLevelColor(container.riskLevel)} size="sm">
                                            {translateRiskLevel(container.riskLevel)}
                                        </Badge>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <span className="text-sm text-tertiary">
                                            {container.value.currency} {container.value.amount.toLocaleString()}
                                        </span>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Link
                                            to={`/receiver/containers/${container.id}`}
                                            className="text-brand-secondary hover:text-brand-secondary_hover"
                                        >
                                            <Eye className="size-4" />
                                        </Link>
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                </TableCard.Root>

                {filteredContainers.length === 0 && (
                    <div className="text-center py-12">
                        <div className="text-tertiary mb-4">
                            <Package className="size-12 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-primary mb-2">{t('detail.containers.noContainers.title')}</h3>
                            <p className="text-tertiary">
                                {searchTerm || statusFilter || riskLevelFilter || documentStatusFilter
                                    ? t('detail.containers.noContainers.message')
                                    : t('detail.containers.noContainers.emptyMessage')
                                }
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
